import React from 'react';
import { Box, Typography, alpha, useTheme, useMediaQuery } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CodeIcon from '@mui/icons-material/Code';
import SchoolIcon from '@mui/icons-material/School';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import VerifiedIcon from '@mui/icons-material/Verified';
import BuildIcon from '@mui/icons-material/Build';
import FlagIcon from '@mui/icons-material/Flag';

/** Career lifecycle stages arranged as a continuous 360° journey around the individual. */
const CAREER_STAGES = [
  { label: 'College project intern', short: 'Project intern', icon: CodeIcon, color: '#818cf8' },
  { label: 'Final-year intern', short: 'Final-year intern', icon: SchoolIcon, color: '#a78bfa' },
  { label: 'Job seeking', short: 'Job seeking', icon: SearchIcon, color: '#c084fc' },
  { label: 'Upskill training', short: 'Upskill training', icon: TrendingUpIcon, color: '#e879f9' },
  { label: 'Job finding', short: 'Job finding', icon: WorkOutlineIcon, color: '#f472b6' },
  { label: 'Getting placed', short: 'Getting placed', icon: EmojiEventsIcon, color: '#fb7185' },
  { label: 'Certification upskilling', short: 'Certifications', icon: VerifiedIcon, color: '#fbbf24' },
  { label: 'Extra projects', short: 'Extra projects', icon: BuildIcon, color: '#34d399' },
  { label: 'Challenges & growth', short: 'Challenges', icon: FlagIcon, color: '#38bdf8' }
];

function stagePosition(index, total, radiusPct) {
  const angleDeg = (index / total) * 360 - 90;
  const rad = (angleDeg * Math.PI) / 180;
  return {
    left: `${50 + radiusPct * Math.cos(rad)}%`,
    top: `${50 + radiusPct * Math.sin(rad)}%`
  };
}

export default function Career360Wheel() {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const size = compact ? 300 : 440;
  const radiusPct = compact ? 38 : 42;

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          position: 'relative',
          width: size,
          height: size,
          maxWidth: '100%',
          mx: 'auto'
        }}
        aria-label="360 degree career journey"
      >
        {/* Outer orbit ring */}
        <Box
          sx={{
            position: 'absolute',
            inset: '4%',
            borderRadius: '50%',
            border: '2px dashed',
            borderColor: alpha('#818cf8', 0.45),
            boxShadow: `inset 0 0 40px ${alpha('#6366f1', 0.12)}`
          }}
        />
        {/* Direction arrow hint (clockwise growth) */}
        <Box
          component="svg"
          viewBox="0 0 100 100"
          sx={{
            position: 'absolute',
            inset: '2%',
            width: '96%',
            height: '96%',
            pointerEvents: 'none',
            opacity: 0.35
          }}
        >
          <defs>
            <marker id="career360-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 Z" fill="#a5b4fc" />
            </marker>
          </defs>
          <circle
            cx="50"
            cy="50"
            r="46"
            fill="none"
            stroke="#a5b4fc"
            strokeWidth="0.6"
            strokeDasharray="4 3"
            markerEnd="url(#career360-arrow)"
          />
        </Box>

        {/* Center — the person at the heart of 360° */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: compact ? 108 : 132,
            height: compact ? 108 : 132,
            borderRadius: '50%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            background: `linear-gradient(145deg, ${alpha('#6366f1', 0.35)} 0%, ${alpha('#312e81', 0.85)} 100%)`,
            border: '2px solid',
            borderColor: alpha('#c7d2fe', 0.5),
            boxShadow: `0 0 32px ${alpha('#6366f1', 0.45)}`,
            zIndex: 2,
            px: 1
          }}
        >
          <PersonOutlineIcon sx={{ fontSize: compact ? 36 : 44, color: '#e0e7ff', mb: 0.25 }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              letterSpacing: 0.06,
              color: '#c7d2fe',
              lineHeight: 1.2,
              fontSize: compact ? '0.65rem' : '0.72rem'
            }}
          >
            360°
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: alpha('#e2e8f0', 0.9),
              lineHeight: 1.25,
              fontSize: compact ? '0.6rem' : '0.68rem'
            }}
          >
            Human journey
          </Typography>
        </Box>

        {/* Stage nodes on the ring */}
        {CAREER_STAGES.map((stage, index) => {
          const pos = stagePosition(index, CAREER_STAGES.length, radiusPct);
          const Icon = stage.icon;
          return (
            <Box
              key={stage.label}
              sx={{
                position: 'absolute',
                ...pos,
                transform: 'translate(-50%, -50%)',
                zIndex: 3,
                width: compact ? 72 : 88,
                textAlign: 'center'
              }}
            >
              <Box
                sx={{
                  width: compact ? 40 : 46,
                  height: compact ? 40 : 46,
                  mx: 'auto',
                  mb: 0.5,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(stage.color, 0.2),
                  border: '2px solid',
                  borderColor: alpha(stage.color, 0.65),
                  boxShadow: `0 4px 12px ${alpha(stage.color, 0.25)}`
                }}
              >
                <Icon sx={{ fontSize: compact ? 20 : 22, color: stage.color }} />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontWeight: 700,
                  color: alpha('#f1f5f9', 0.95),
                  lineHeight: 1.25,
                  fontSize: compact ? '0.58rem' : '0.68rem'
                }}
              >
                {compact ? stage.short : stage.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
